import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowClockwise, ClockHistory, Inbox, Search } from 'react-bootstrap-icons'
import Modal from '../../Modal/Modal'
import axiosService from '../../../utils/AxiosService'
import { toastError } from '../../FormComponents/Toasts'
import UnderlineTabs from '../Campaigns/CampaignsBrowseParts/UnderlineTabs'
import WaitingListRow from './WaitingListParts/WaitingListRow'
import ProcessedRow from './WaitingListParts/ProcessedRow'
import {
  POLL_INTERVAL_MS,
  PROCESSED_COLUMNS,
  RESULT_FILTERS,
  TABS,
  WAITING_COLUMNS,
  matchesSearch,
} from './WaitingListParts/waitingListConstants'

const HEADER_CLASSES = 'flex items-center gap-4 px-4 py-2 bg-rb-grey dark:bg-rb-dark-mode-lighter text-xs font-semibold uppercase text-rb-mat-grey dark:text-rb-dark-mode-text-grey sticky top-0 z-10'

function CountPill({ count, active }) {
  return (
    <span className={`ml-2 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${
      active ? 'bg-rb-green text-white dark:text-rb-dark-mode' : 'bg-gray-100 text-rb-mat-grey dark:bg-rb-dark-mode-lighter dark:text-rb-dark-mode-text-grey'
    }`}>
      {count}
    </span>
  )
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <Icon className="text-3xl text-rb-mat-grey dark:text-rb-dark-mode-text-grey" />
      <p className="text-sm font-semibold text-rb-black dark:text-white">{title}</p>
      {description && <p className="text-xs text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{description}</p>}
    </div>
  )
}

function LoadingRows() {
  return (
    <div className="divide-y divide-rb-widget-border dark:divide-rb-dark-mode-lighter">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 px-4 py-3">
          <div className="h-3 w-1/4 rounded bg-gray-200 dark:bg-rb-dark-mode-lighter animate-pulse" />
          <div className="h-3 w-1/3 rounded bg-gray-200 dark:bg-rb-dark-mode-lighter animate-pulse" />
          <div className="h-3 flex-1 rounded bg-gray-200 dark:bg-rb-dark-mode-lighter animate-pulse" />
        </div>
      ))}
    </div>
  )
}

export default function WaitingListModal({ isOpen, setIsOpen }) {
  const [activeTab, setActiveTab] = useState(TABS.waiting)
  const [resultFilter, setResultFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [waiting, setWaiting] = useState([])
  const [processed, setProcessed] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const close = () => setIsOpen(false)

  const fetchAll = useCallback((silent = false) => {
    if (!silent) setRefreshing(true)
    return Promise.all([
      axiosService.get('/api/project_manager/authentications/modash_processables'),
      axiosService.get('/api/project_manager/authentications/modash_processing_logs'),
    ])
      .then(([waitingResponse, processedResponse]) => {
        setWaiting(waitingResponse)
        setProcessed(processedResponse)
      })
      .catch((error) => {
        console.error('Error fetching waiting list:', error)
        if (!silent) toastError('An error occurred while loading the waiting list')
      })
      .finally(() => {
        setLoaded(true)
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setLoaded(false)
      setSearch('')
      setActiveTab(TABS.waiting)
      setResultFilter('all')
      return
    }

    fetchAll()
    const interval = setInterval(() => fetchAll(true), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isOpen, fetchAll])

  const filteredWaiting = useMemo(
    () => waiting.map((row, index) => ({ row, position: index + 1 })).filter(({ row }) => matchesSearch(row, search)),
    [waiting, search],
  )

  const resultCounts = useMemo(() => ({
    all: processed.length,
    processed: processed.filter(row => row.status === 'processed').length,
    failed: processed.filter(row => row.status === 'failed').length,
  }), [processed])

  const filteredProcessed = useMemo(
    () => processed.filter(row => (resultFilter === 'all' || row.status === resultFilter) && matchesSearch(row, search)),
    [processed, resultFilter, search],
  )

  const tabs = [
    { id: TABS.waiting, label: <span className="inline-flex items-center">Waiting list<CountPill count={waiting.length} active={activeTab === TABS.waiting} /></span> },
    { id: TABS.processed, label: <span className="inline-flex items-center">Recently processed<CountPill count={processed.length} active={activeTab === TABS.processed} /></span> },
  ]

  const renderWaiting = () => (
    <>
      <div className={HEADER_CLASSES}>
        <span className={WAITING_COLUMNS.position}>#</span>
        <span className={WAITING_COLUMNS.account}>Account</span>
        <span className={WAITING_COLUMNS.email}>Email</span>
        <span className={WAITING_COLUMNS.type}>Report</span>
        <span className={WAITING_COLUMNS.requestedBy}>Requested by</span>
        <span className={WAITING_COLUMNS.queued}>Queued</span>
        <span className={WAITING_COLUMNS.status}>Status</span>
      </div>
      {filteredWaiting.length === 0 ? (
        search ? (
          <EmptyState icon={Search} title="No matches" description="No queued accounts match your search." />
        ) : (
          <EmptyState icon={Inbox} title="The waiting list is empty" description="Accounts pulled from Modash show up here until they are processed." />
        )
      ) : (
        filteredWaiting.map(({ row, position }) => (
          <WaitingListRow key={row.id} row={row} position={position} onNavigate={close} />
        ))
      )}
    </>
  )

  const renderProcessed = () => (
    <>
      <div className={HEADER_CLASSES}>
        <span className={PROCESSED_COLUMNS.chevron} />
        <span className={PROCESSED_COLUMNS.account}>Account</span>
        <span className={PROCESSED_COLUMNS.email}>Email</span>
        <span className={PROCESSED_COLUMNS.type}>Report</span>
        <span className={PROCESSED_COLUMNS.requestedBy}>Requested by</span>
        <span className={PROCESSED_COLUMNS.finished}>Finished</span>
        <span className={PROCESSED_COLUMNS.status}>Result</span>
      </div>
      {filteredProcessed.length === 0 ? (
        <EmptyState
          icon={search ? Search : ClockHistory}
          title={search ? 'No matches' : 'Nothing processed recently'}
          description={search ? 'No processed accounts match your search.' : 'Processed and failed accounts from the last 7 days appear here.'}
        />
      ) : (
        filteredProcessed.map(row => <ProcessedRow key={row.id} row={row} onNavigate={close} />)
      )}
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <Modal.Header>
        <div className="flex flex-col">
          <span className="font-bold">Influencer Waiting List</span>
          <span className="text-xs font-normal text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
            Modash reports are processed in batches roughly every 5 minutes
          </span>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="w-[min(1100px,calc(100vw-6rem))] pb-2">
          <div className="flex items-end justify-between gap-4 border-b border-rb-widget-border dark:border-rb-dark-mode-lighter">
            <div><UnderlineTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} /></div>
            <div className="flex items-center gap-2 pb-2">
              {activeTab === TABS.processed && (
                <div className="inline-flex rounded-lg bg-rb-grey p-0.5 dark:bg-rb-dark-mode-lighter">
                  {RESULT_FILTERS.map(filter => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setResultFilter(filter.value)}
                      className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                        resultFilter === filter.value
                          ? 'bg-white text-rb-black shadow-sm dark:bg-rb-dark-mode dark:text-white'
                          : 'text-rb-mat-grey hover:text-rb-black dark:text-rb-dark-mode-text-grey dark:hover:text-white'
                      }`}
                    >
                      {filter.label}
                      <span className={`ml-1.5 ${filter.value === 'failed' && resultCounts.failed > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {resultCounts[filter.value]}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-rb-mat-grey dark:text-rb-dark-mode-text-grey" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, handle, email…"
                  className="w-64 rounded-lg border-1 border-rb-widget-border bg-white py-1.5 pl-8 pr-3 text-sm text-rb-black placeholder:text-rb-mat-grey focus:border-rb-green focus:outline-none dark:border-rb-dark-mode-lighter dark:bg-rb-dark-mode dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={() => fetchAll()}
                disabled={refreshing}
                title="Refresh"
                className="rounded-lg border-1 border-rb-widget-border p-2 text-rb-mat-grey hover:bg-rb-grey hover:text-rb-black disabled:opacity-60 dark:border-rb-dark-mode-lighter dark:text-rb-dark-mode-text-grey dark:hover:bg-rb-dark-mode-lighter dark:hover:text-white"
              >
                <ArrowClockwise className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter overflow-hidden">
            <div className="h-[calc(100vh-360px)] min-h-[240px] overflow-y-auto">
              {!loaded ? <LoadingRows /> : activeTab === TABS.waiting ? renderWaiting() : renderProcessed()}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
